import React from 'react';
import {
  Box,
  Checkbox,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HelpIcon from '@mui/icons-material/Help';
import { money } from './format.js';
import {
  brandLabel,
  groupedVarianceRows,
  varianceDriverLabel,
} from './varianceSummaryTableModel.js';
import { isDraftVarianceAction } from './varianceActionState.js';

const fileFindingCount = (finding) => (
  Number(finding?.errors || 0) + Number(finding?.warnings || 0)
);

const optionalMoney = (value) => (value === undefined || value === null ? '--' : money(value));

const FileFindingCell = ({ finding }) => {
  const count = fileFindingCount(finding);
  return (
    <Typography
      variant="body2"
      sx={{
        color: count ? 'warning.dark' : 'text.secondary',
        fontWeight: 800,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {count}
    </Typography>
  );
};

const ReviewedCell = ({ reviewed }) => (
  <Tooltip title={reviewed ? 'Reviewed' : 'Not reviewed'}>
    <Box sx={{ display: 'inline-flex', color: reviewed ? 'success.main' : 'error.main' }}>
      {reviewed ? <CheckCircleIcon fontSize="small" /> : <CancelIcon fontSize="small" />}
    </Box>
  </Tooltip>
);

const normalizeEnrollmentStatus = (status) => String(status || '')
  .trim()
  .toUpperCase()
  .replace(/[\s-]+/g, '_');

const DPE_ENROLLMENT_ICON_META = {
  ENROLLED: { label: 'Enrolled', Icon: CheckCircleIcon, color: 'success.main' },
  IN_PROGRESS: { label: 'In Progress', Icon: CheckCircleIcon, color: 'info.main' },
  ACTION_REQUIRED: { label: 'Action Required', Icon: CheckCircleIcon, color: 'warning.main' },
  PENDING_PROVIDER_CHANGE: { label: 'Pending Provider Change', Icon: CheckCircleIcon, color: 'warning.dark' },
  CANCELLED: { label: 'Cancelled', Icon: CancelIcon, color: 'error.main' },
  CANCELED: { label: 'Cancelled', Icon: CancelIcon, color: 'error.main' },
  OPTED_OUT: { label: 'Opted Out', Icon: CancelIcon, color: 'warning.dark' },
};

const DpeEnrollmentIcon = ({ status }) => {
  const normalized = normalizeEnrollmentStatus(status);
  const meta = DPE_ENROLLMENT_ICON_META[normalized] || {
    label: normalized ? String(status).trim() : 'DPE enrollment unavailable',
    Icon: HelpIcon,
    color: normalized ? 'text.secondary' : 'error.main',
  };
  const { Icon, label, color } = meta;
  return (
    <Tooltip title={`DPE enrollment: ${label}`}>
      <Box
        aria-label={`DPE enrollment: ${label}`}
        role="img"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          color,
          lineHeight: 0,
        }}
      >
        <Icon fontSize="small" />
      </Box>
    </Tooltip>
  );
};

const SortableHeadCell = ({ id, label, align = 'left', sortBy, sortDirection, onSortChange, sx }) => (
  <TableCell align={align} sx={sx}>
    <TableSortLabel
      active={sortBy === id}
      direction={sortBy === id ? sortDirection : 'asc'}
      onClick={() => onSortChange?.(id)}
    >
      {label}
    </TableSortLabel>
  </TableCell>
);

const DriverCell = ({ row }) => (
  <Stack spacing={0.25}>
    <Typography variant="body2" sx={{ fontWeight: 700 }}>
      {varianceDriverLabel(row)}
    </Typography>
    {row.varianceDriver?.detail && (
      <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10.5, fontStyle: 'italic' }}>
        {row.varianceDriver.detail}
      </Typography>
    )}
  </Stack>
);

const ModifiedAmountCell = ({ value, originalValue, modified = false, label }) => {
  const displayValue = money(value);
  return (
    <Tooltip
      title={modified ? `${label} modified from ${money(originalValue)} to ${displayValue}` : ''}
      disableHoverListener={!modified}
      describeChild
    >
      <Typography
        variant="body2"
        data-modified-total={modified ? 'true' : undefined}
        sx={{
          color: modified ? 'warning.main' : 'text.primary',
          fontWeight: modified ? 900 : 400,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {displayValue}
      </Typography>
    </Tooltip>
  );
};

const amountModified = (row = {}, fileKind) => Boolean(row.totalStatuses?.[fileKind]);

const accountDisplayName = (summary = {}) => {
  const displayName = summary.displayName || '';
  const accounts = Array.isArray(summary.accounts) ? summary.accounts : [];
  if (!/^\d+\s+Salesforce accounts$/i.test(displayName)) return displayName;
  const names = accounts.map((account) => account.salesforceAccountName).filter(Boolean);
  if (!names.length) return displayName;
  return names.length === 1 ? names[0] : `${names[0]} +${names.length - 1}`;
};

const hasDraftVarianceAction = (row = {}) => (
  Array.isArray(row.varianceActions)
    && row.varianceActions.some((action) => isDraftVarianceAction(action))
);

const rowWarmPending = (row = {}) => {
  const status = String(row.contextWarmStatus || '').trim().toLowerCase();
  return ['pending', 'queued', 'processing', 'invalidated'].includes(status);
};

const rowWarmProblem = (row = {}) => {
  const status = String(row.contextWarmStatus || '').trim().toLowerCase();
  return ['failed', 'unavailable'].includes(status);
};

const WarmStatusChip = ({ row }) => {
  const status = String(row.contextWarmStatus || '').trim().toLowerCase();
  if (!status || status === 'processed' || status === 'unknown') return null;
  const stale = Boolean(row.contextWarmStale);
  const labels = {
    pending: 'Context pending',
    queued: 'Refresh queued',
    processing: 'Refreshing snapshot',
    invalidated: 'Refresh needed',
    failed: 'Context failed',
    unavailable: 'Context unavailable',
  };
  const statusUpdatedAt = row.contextWarmUpdatedAt || row.contextWarmLastAttemptAt || row.contextWarmStartedAt || null;
  const label = stale ? 'Warm stale' : labels[status] || status;
  const tooltip = row.contextWarmError || (stale
    ? `Warm status has not updated recently${statusUpdatedAt ? ` since ${statusUpdatedAt}` : ''}. Existing warmed snapshot remains available when present.`
    : (
    row.contextWarmedAt
      ? `${labels[status] || status}. Existing warmed snapshot remains available from ${row.contextWarmedAt}.`
      : labels[status] || status
    ));
  return (
    <Tooltip title={tooltip}>
      <Chip
        size="small"
        color={rowWarmProblem(row) || stale ? 'error' : 'warning'}
        variant="outlined"
        label={label}
        sx={{ mt: 0.5, height: 18, fontSize: 10, maxWidth: 170 }}
      />
    </Tooltip>
  );
};

const VarianceTable = ({
  rows,
  selectedBac,
  drawerOpen,
  onRowClick,
  fileHealthByBac = {},
  groupBy = 'none',
  sortBy = 'maxAbsDelta',
  sortDirection = 'desc',
  onSortChange = null,
  selectedVarianceIds = new Set(),
  allVisibleSelected = false,
  someVisibleSelected = false,
  onToggleVarianceSelection = null,
  onToggleAllVisibleVariances = null,
}) => (
  <TableContainer>
    <Table
      size="small"
      sx={{
        '& .MuiTableCell-root': {
          py: 1.25,
          borderBottomColor: 'divider',
        },
        '& .MuiTableCell-head': {
          fontWeight: 700,
          fontSize: 11,
          letterSpacing: 0.5,
          textTransform: 'uppercase',
          color: 'text.secondary',
          bgcolor: 'action.hover',
          py: 1,
        },
      }}
    >
      <TableHead>
        <TableRow>
          <TableCell padding="checkbox">
            <Checkbox
              size="small"
              aria-label="Select all visible variances"
              checked={allVisibleSelected}
              indeterminate={someVisibleSelected}
              onChange={(event) => onToggleAllVisibleVariances?.(event.target.checked)}
            />
          </TableCell>
          <SortableHeadCell id="bac" label="BAC" sortBy={sortBy} sortDirection={sortDirection} onSortChange={onSortChange} />
          <SortableHeadCell id="dealer" label="Dealer" sortBy={sortBy} sortDirection={sortDirection} onSortChange={onSortChange} />
          <TableCell sx={{ whiteSpace: 'nowrap' }}>Findings</TableCell>
          <SortableHeadCell id="brand" label="Brand" sortBy={sortBy} sortDirection={sortDirection} onSortChange={onSortChange} />
          <SortableHeadCell id="enrollment" label="Enrollment" sortBy={sortBy} sortDirection={sortDirection} onSortChange={onSortChange} />
          <SortableHeadCell id="driver" label="Variance driver" sortBy={sortBy} sortDirection={sortDirection} onSortChange={onSortChange} />
          <SortableHeadCell id="reviewed" label="Reviewed" sortBy={sortBy} sortDirection={sortDirection} onSortChange={onSortChange} />
          <SortableHeadCell id="gmTotal" label="GM" align="right" sortBy={sortBy} sortDirection={sortDirection} onSortChange={onSortChange} />
          <SortableHeadCell id="cpqTotal" label="CPQ" align="right" sortBy={sortBy} sortDirection={sortDirection} onSortChange={onSortChange} />
          <SortableHeadCell id="dpeTotal" label="DPE" align="right" sortBy={sortBy} sortDirection={sortDirection} onSortChange={onSortChange} />
          <SortableHeadCell id="appSheetTotal" label="AppSheet" align="right" sortBy={sortBy} sortDirection={sortDirection} onSortChange={onSortChange} />
        </TableRow>
      </TableHead>
      <TableBody>
        {groupedVarianceRows(rows, groupBy).flatMap(([group, groupRows]) => {
          const groupRow = group ? [(
            <TableRow key={`group-${group}`}>
              <TableCell colSpan={12} sx={{ bgcolor: 'action.hover', fontWeight: 900 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" sx={{ fontWeight: 900 }}>{group}</Typography>
                  <Chip size="small" label={`${groupRows.length} BAC${groupRows.length === 1 ? '' : 's'}`} />
                </Stack>
              </TableCell>
            </TableRow>
          )] : [];
          return groupRow.concat(groupRows.map((row) => {
          const reviewed = row.review?.reviewed;
          const varianceId = row.id || row.varianceId;
          const warmPending = rowWarmPending(row);
          const warmProblem = rowWarmProblem(row);
          const isSelected = row.bac === selectedBac && drawerOpen;
          const healthFinding = fileHealthByBac[String(row.bac)];
          const accountSummary = row.accountMatchSummary || {};
          const hasDraftAction = hasDraftVarianceAction(row);
          const dealerDisplayName = row.dealerDisplayName
            || row.dealerName
            || row.dpeDealerName
            || accountDisplayName(accountSummary)
            || '—';
          const displayBrand = brandLabel(row);
          return (
            <TableRow
              key={row.bac}
              hover
              onClick={() => onRowClick(row.bac)}
              selected={isSelected}
              data-billing-context-warm-status={row.contextWarmStatus || 'unknown'}
              sx={{
                cursor: 'pointer',
                opacity: warmPending ? 0.48 : warmProblem ? 0.82 : reviewed ? 0.78 : 1,
                transition: 'background-color 120ms, opacity 120ms, box-shadow 120ms',
                '&.Mui-selected': {
                  bgcolor: (t) =>
                    t.palette.mode === 'dark'
                      ? 'rgba(25, 118, 210, 0.18)'
                      : 'rgba(25, 118, 210, 0.08)',
                },
                '&.Mui-selected:hover': {
                  bgcolor: (t) =>
                    t.palette.mode === 'dark'
                      ? 'rgba(25, 118, 210, 0.24)'
                      : 'rgba(25, 118, 210, 0.12)',
                },
                '&:last-child td': { border: 0 },
              }}
            >
              <TableCell padding="checkbox" onClick={(event) => event.stopPropagation()}>
                <Checkbox
                  size="small"
                  aria-label={`Select variance ${row.bac}`}
                  checked={Boolean(varianceId && selectedVarianceIds.has(String(varianceId)))}
                  onChange={(event) => onToggleVarianceSelection?.(row, event.target.checked)}
                />
              </TableCell>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: 12.5, color: 'text.secondary' }}>
                {row.bac}
              </TableCell>
              <TableCell>
                <Box>
                  <Tooltip
                    title={hasDraftAction ? 'This BAC has an unsaved draft action.' : ''}
                    disableHoverListener={!hasDraftAction}
                    describeChild
                  >
                    <Typography
                      variant="body2"
                      data-draft-warning={hasDraftAction ? 'true' : undefined}
                      sx={{
                        color: hasDraftAction ? 'warning.main' : 'text.primary',
                        fontWeight: 600,
                        lineHeight: 1.3,
                      }}
                    >
                      {dealerDisplayName}
                    </Typography>
                  </Tooltip>
                  <WarmStatusChip row={row} />
                  {row.review?.inheritedResolved ? (
                    <Chip
                      size="small"
                      color="success"
                      variant="outlined"
                      label="Resolved from prior round"
                      sx={{ mt: 0.5, height: 20, fontSize: 11, fontWeight: 800 }}
                    />
                  ) : null}
                </Box>
              </TableCell>
              <TableCell>
                <FileFindingCell finding={healthFinding} />
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {displayBrand || '—'}
                </Typography>
              </TableCell>
              <TableCell>
                <DpeEnrollmentIcon status={row.dpeEnrollmentStatusCd || row.dpe_enrollment_status_cd} />
              </TableCell>
              <TableCell>
                <DriverCell row={row} />
              </TableCell>
              <TableCell>
                <ReviewedCell reviewed={reviewed} />
              </TableCell>
              <TableCell align="right">
                <ModifiedAmountCell
                  value={row.gmTotal}
                  originalValue={row.originalTotals?.gmTotal}
                  modified={amountModified(row, 'gm')}
                  label="GM total"
                />
              </TableCell>
              <TableCell align="right">
                <ModifiedAmountCell
                  value={row.cpqTotal}
                  originalValue={row.originalTotals?.cpqTotal}
                  modified={amountModified(row, 'cpq')}
                  label="CPQ total"
                />
              </TableCell>
              <TableCell align="right">
                <ModifiedAmountCell
                  value={row.dpeTotal}
                  originalValue={row.originalTotals?.dpeTotal}
                  modified={amountModified(row, 'dpe')}
                  label="DPE total"
                />
              </TableCell>
              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {optionalMoney(row.appSheetTotal ?? row.app_sheet_total)}
              </TableCell>
            </TableRow>
          );
          }));
        })}
      </TableBody>
    </Table>
  </TableContainer>
);

export default VarianceTable;
